import DOMPurify from 'isomorphic-dompurify';

// XSS Protection - Sanitize HTML content
export const sanitizeHTML = (dirty) => {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br'],
    ALLOWED_ATTR: ['href', 'target', 'rel']
  });
};

// Input validation helpers
export const validators = {
  email: (email) => {
    const re = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return re.test(email);
  },
  
  password: (password) => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special character
    const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return {
      isValid: re.test(password),
      errors: {
        length: password.length < 8,
        uppercase: !/[A-Z]/.test(password),
        lowercase: !/[a-z]/.test(password),
        number: !/\d/.test(password),
        special: !/[@$!%*?&]/.test(password)
      }
    };
  },
  
  phoneNumber: (phone) => {
    const re = /^\+?[1-9]\d{1,14}$/;
    return re.test(phone.replace(/\s/g, ''));
  },
  
  alphanumeric: (str) => {
    const re = /^[a-zA-Z0-9\s]+$/;
    return re.test(str);
  },
  
  url: (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }
};

// Sanitize user input
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  // Remove any HTML tags
  let sanitized = input.replace(/<[^>]*>/g, '');
  
  // Escape special characters
  sanitized = sanitized
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
  
  // Remove any potential SQL injection attempts
  sanitized = sanitized.replace(/(\b(SELECT|INSERT|UPDATE|DELETE|DROP|UNION|ALTER|CREATE)\b)/gi, '');
  
  return sanitized.trim();
};

// Rate limiting for API calls
class RateLimiter {
  constructor(maxRequests = 10, windowMs = 60000) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
    this.requests = new Map();
  }
  
  isAllowed(key) {
    const now = Date.now();
    const userRequests = this.requests.get(key) || [];
    
    // Remove old requests outside the window
    const validRequests = userRequests.filter(time => now - time < this.windowMs);
    
    if (validRequests.length >= this.maxRequests) {
      return false;
    }
    
    validRequests.push(now);
    this.requests.set(key, validRequests);
    
    // Cleanup old entries
    if (this.requests.size > 1000) {
      const oldestAllowed = now - this.windowMs;
      for (const [k, times] of this.requests.entries()) {
        if (times.every(t => t < oldestAllowed)) {
          this.requests.delete(k);
        }
      }
    }
    
    return true;
  }
}

export const apiRateLimiter = new RateLimiter(30, 60000); // 30 requests per minute

// Secure headers configuration
export const securityHeaders = {
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'DENY',
  'X-XSS-Protection': '1; mode=block',
  'Strict-Transport-Security': 'max-age=31536000; includeSubDomains',
  'Content-Security-Policy': "default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval'; style-src 'self' 'unsafe-inline'; img-src 'self' data: https: http:; font-src 'self' data:; connect-src 'self' https://api.secureroom.ai;"
};

// CSRF Token management
export const csrfToken = {
  generate: () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  },
  
  store: (token) => {
    sessionStorage.setItem('csrf_token', token);
  },
  
  get: () => {
    return sessionStorage.getItem('csrf_token');
  },
  
  validate: (token) => {
    const storedToken = sessionStorage.getItem('csrf_token');
    return storedToken && storedToken === token;
  }
};

// Secure storage wrapper
export const secureStorage = {
  setItem: (key, value, encrypt = false) => {
    try {
      let data = typeof value === 'object' ? JSON.stringify(value) : value;
      
      if (encrypt) {
        // Basic encoding - in production, use proper encryption
        data = btoa(encodeURIComponent(data));
      }
      
      localStorage.setItem(key, data);
    } catch (error) {
    }
  },
  
  getItem: (key, decrypt = false) => {
    try {
      let data = localStorage.getItem(key);
      
      if (!data) return null;
      
      if (decrypt) {
        data = decodeURIComponent(atob(data));
      }
      
      try {
        return JSON.parse(data);
      } catch {
        return data;
      }
    } catch (error) {
      return null;
    }
  },
  
  removeItem: (key) => {
    localStorage.removeItem(key);
  },
  
  clear: () => {
    localStorage.clear();
    sessionStorage.clear();
  }
};

// Session timeout management
export class SessionManager {
  constructor(timeoutMinutes = 30) {
    this.timeoutMinutes = timeoutMinutes;
    this.timeoutId = null;
    this.warningTimeoutId = null;
  }
  
  start(onTimeout, onWarning) {
    this.reset();
    
    // Warning 5 minutes before timeout
    const warningTime = (this.timeoutMinutes - 5) * 60 * 1000;
    const timeoutTime = this.timeoutMinutes * 60 * 1000;
    
    if (warningTime > 0) {
      this.warningTimeoutId = setTimeout(() => {
        if (onWarning) onWarning();
      }, warningTime);
    }
    
    this.timeoutId = setTimeout(() => {
      if (onTimeout) onTimeout();
    }, timeoutTime);
    
    // Reset on user activity
    ['mousedown', 'keypress', 'scroll', 'touchstart'].forEach(event => {
      document.addEventListener(event, () => this.reset(), true);
    });
  }
  
  reset() {
    if (this.timeoutId) clearTimeout(this.timeoutId);
    if (this.warningTimeoutId) clearTimeout(this.warningTimeoutId);
  }
  
  stop() {
    this.reset();
  }
}

// Password strength checker
export const checkPasswordStrength = (password) => {
  let strength = 0;
  const feedback = [];
  
  if (password.length >= 8) strength += 1;
  else feedback.push('Use at least 8 characters');
  
  if (password.length >= 12) strength += 1;
  
  if (/[a-z]/.test(password)) strength += 1;
  else feedback.push('Add lowercase letters');
  
  if (/[A-Z]/.test(password)) strength += 1;
  else feedback.push('Add uppercase letters');
  
  if (/\d/.test(password)) strength += 1;
  else feedback.push('Add numbers');
  
  if (/[@$!%*?&]/.test(password)) strength += 1;
  else feedback.push('Add special characters');
  
  const strengthLevels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
  
  return {
    score: strength,
    level: strengthLevels[Math.min(strength, strengthLevels.length - 1)],
    feedback
  };
};

// Sanitize file uploads
export const validateFileUpload = (file, options = {}) => {
  const {
    maxSize = 10 * 1024 * 1024, // 10MB default
    allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
    allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.pdf']
  } = options;
  
  const errors = [];
  
  // Check file size
  if (file.size > maxSize) {
    errors.push(`File size exceeds ${maxSize / (1024 * 1024)}MB limit`);
  }
  
  // Check file type
  if (!allowedTypes.includes(file.type)) {
    errors.push('File type not allowed');
  }
  
  // Check file extension
  const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  if (!allowedExtensions.includes(extension)) {
    errors.push('File extension not allowed');
  }
  
  // Check for potential malicious file names
  if (!/^[\w\-. ]+$/.test(file.name)) {
    errors.push('Invalid file name');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

const SecurityUtils = {
  sanitizeHTML,
  sanitizeInput,
  validators,
  apiRateLimiter,
  securityHeaders,
  csrfToken,
  secureStorage,
  SessionManager,
  checkPasswordStrength,
  validateFileUpload
};

export default SecurityUtils;